import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box } from '@mui/material';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { useRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';
import { sspSubmitOrder, toggleSspModels, showPreviewOrder } from 'pages/provisioning/day2/github-onboarding-new/store';
import ProvisioningStyles from 'pages/provisioning/day2/github-onboarding-new/style';
import { catalogs } from 'store';
import { formOrderObjFromOrderDetails, resetOrder, resetToggleSspModal } from 'pages/provisioning/day2/github-onboarding-new/utils';
import { useResetConfigurations, useInitializeAtomsFromOrderDetails } from 'pages/provisioning/day2/github-onboarding-new/utils/hooks';
import getProvisioningTitle from 'utils/getProvisioningTitle';
import { CustomLoader } from 'pages/provisioning/day2/github-onboarding-new/components/common';

const ProvisioningHeader = React.lazy(() => import('pages/provisioning/day2/github-onboarding-new/ProvisioningHeader'));
const PreviewOrderPage = React.lazy(() => import('pages/provisioning/day2/github-onboarding-new/components/preview'));
const PreviewProvisioiningDetails = React.lazy(() => import('pages/provisioning/day2/github-onboarding-new/components/preview/PreviewProvisioningDetails'));
const SspForm = React.lazy(() => import('pages/provisioning/day2/github-onboarding-new/SspForm'));
const ShowCostDetails = React.lazy(() => import('pages/provisioning/day2/github-onboarding-new/ShowCostDetails'));
const DialogBox = React.lazy(() => import('pages/provisioning/day2/github-onboarding-new/components/dialog-box/SubmitOrderSuccessModel'));

const GithubOnboardingNewPage = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [cmpCatalogs] = useRecoilState(catalogs);
    const [order, setOrder] = useRecoilState(sspSubmitOrder);
    const [toggleSspModel, resetToggleSspModels] = useRecoilState(toggleSspModels);
    const [showPreview, setShowPreview] = useRecoilState(showPreviewOrder);
    const { resetConfigurations } = useResetConfigurations();
    const { initializeAtomsFromOrderDetails } = useInitializeAtomsFromOrderDetails();
    const { catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const axios = useAxios();

    async function getCosts() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}/costs`);
        return data;
    }

    async function checkCatalog() {
        const data = cmpCatalogs?.find((item) => item?.catalogDefinitionId === catalogCode);
        const costs = await getCosts();
        if (cartItemId && operation && catalogServiceId) {
            const { data: orderDetails } = await axios.get(`${endPoints.riServices.Jwt.orderDetails}/${cartItemId}`);
            const orderObj = await formOrderObjFromOrderDetails(catalogServiceId, orderDetails);
            const catalog = orderDetails?.catalogServices?.find((item) => item?.catalogServiceId === catalogServiceId) || null;
            const catalogConfig = catalog.catalogConfig || null;
            await initializeAtomsFromOrderDetails(catalogConfig, orderObj);
            let capability;
            let capabilityDetails;
            if (orderObj.capability) {
                const response = await axios.get(endPoints.day2Operation.GithubOnboarding.capabilityTypes);
                const capabilityData = response?.data;
                if (Array.isArray(capabilityData)) {
                    const matched = capabilityData.find((item) => item.backendName === orderObj.capability);
                    if (matched) {
                        capability = matched.displayName;
                        capabilityDetails = {
                            label: matched.displayName,
                            value: matched.backendName,
                            data: matched,
                        };
                    }
                }
            }
            if (operation === 'preview') {
                setShowPreview('preview');
                setOrder((previous) => ({
                    ...previous,
                    ...orderObj,
                    orderId: cartItemId,
                    currentOrderStatus: orderDetails?.currentStatus?.status,
                    createdBy: orderDetails?.created?.by?.id,
                    applicationManager: orderDetails?.manager?.name,
                    activeDirectoryGroupOwner: orderDetails?.activeDirectoryGroupOwner?.name,
                    githubOwner: orderDetails?.adoOwner?.name,
                    catalogType: data?.displayName,
                }));
            } else {
                setOrder((previous) => ({
                    ...previous,
                    ...orderObj,
                    capability,
                    capabilityDetails,
                    emptyCost: costs,
                }));
            }
        } else {
            setOrder((previous) => ({
                ...previous,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data?.cloudService,
                validationStatus: 'clearValidation',
                isTouched: false,
                emptyCost: costs,
            }));
        }

        setLoading(false);
    }

    const reset = () => {
        if (catalogCode) {
            setOrder({ ...order, ...resetOrder });
            resetToggleSspModels(resetToggleSspModal);
            resetConfigurations();
            setShowPreview(null);
        }
    };

    React.useEffect(() => {
        if (cmpCatalogs?.length && catalogCode) {
            setLoading(true);
            reset();
            checkCatalog();
        }
    }, [cmpCatalogs, catalogCode]);

    const handleReset = () => {
        setOrder(resetOrder);
        resetToggleSspModels();
        resetConfigurations();
        setShowPreview(null);
    };

    React.useEffect(() => {
        if (operation === 'preview') {
            setShowPreview('preview');
        } else if (operation === 'edit') {
            setShowPreview(null);
        } else {
            reset();
            setShowPreview(null);
        }
    }, [operation]);

    React.useEffect(() => {
        return () => reset();
        // eslint-disable-next-line
    }, []);
    return (
        <>
            <Helmet>
                <title>{getProvisioningTitle(cmpCatalogs, catalogCode)}</title>
            </Helmet>
            <Suspense
                fallback={
                    <CustomLoader
                        size={16}
                        message="Loading...."
                        spinnerSx={ProvisioningStyles.circularProgressColor}
                        messageSx={ProvisioningStyles.circularProgressColor}
                        sx={ProvisioningStyles.provisioning_wrapper}
                    />
                }
            >
                {isLoading && (
                    <CustomLoader
                        size={16}
                        message="Loading...."
                        spinnerSx={ProvisioningStyles.circularProgressColor}
                        messageSx={ProvisioningStyles.circularProgressColor}
                        sx={ProvisioningStyles.provisioning_wrapper}
                    />
                )}
                {!isLoading && (
                    <Box sx={ProvisioningStyles.provisioningBox}>
                        <ProvisioningHeader />
                        {showPreview === 'preview' && <PreviewProvisioiningDetails />}
                        {showPreview === 'pre-preview-order' && <PreviewOrderPage />}
                        {!showPreview && (
                            <Box sx={ProvisioningStyles.sspform_wrapper} className="contentContainer">
                                <SspForm />
                            </Box>
                        )}
                        <br />
                        <ShowCostDetails />
                        <DialogBox isOpen={toggleSspModel?.isSubmitOrderSuccess} reset={() => handleReset()} redirectUrl="/" operation="success" />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default GithubOnboardingNewPage;
